import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Minus, Plus } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCountry } from '../context/CountryContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const orderDataFromState = location.state;
  const { user } = useAuth();
  const { formatPrice, selectedCountry, convertPrice } = useCountry();
  
  // Check for digital card customization data from sessionStorage
  const [orderData, setOrderData] = useState(null);

  const [quantity, setQuantity] = useState(1);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [paymentType, setPaymentType] = useState(''); // Empty default - user must select

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  const [shippingAddress, setShippingAddress] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
  });

  const [billingAddress, setBillingAddress] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
  });

  // Pre-fill email from logged-in user
  useEffect(() => {
    if (user && user.email) {
      setShippingAddress(prev => ({
        ...prev,
        email: user.email
      }));
      setBillingAddress(prev => ({
        ...prev,
        email: user.email
      }));
    }
  }, [user]);

  useEffect(() => {
    console.log('CheckoutPage useEffect running...');
    
    // Prevent double execution in React StrictMode
    if (orderData) {
      console.log('orderData already set, skipping useEffect');
      return;
    }
    
    // Check for digital card customization data first
    const digitalCardData = sessionStorage.getItem('cardCustomization');
    console.log('digitalCardData from sessionStorage:', digitalCardData);
    
    if (digitalCardData) {
      // Digital card customization flow
      try {
        const customizationData = JSON.parse(digitalCardData);
        console.log('Parsed customization data:', customizationData);
        setOrderData(customizationData);
        
        // Fetch digital card details to get material information
        if (customizationData.cardId) {
          fetchDigitalCardMaterial(customizationData.cardId);
        }
        
        console.log('Digital card order data set successfully');
      } catch (error) {
        console.error('Error parsing digitalCardData:', error);
        navigate('/review-cards');
      }
    } else if (orderDataFromState) {
      // Review card flow
      console.log('Using orderDataFromState:', orderDataFromState);
      setOrderData(orderDataFromState);
      
      if (orderDataFromState.card && orderDataFromState.card.materials && orderDataFromState.card.materials.length > 0) {
        // Set default material to first one
        setSelectedMaterial(orderDataFromState.card.materials[0]);
      }
    } else {
      // No order data, redirect to review cards
      console.log('No order data found, redirecting to /review-cards');
      navigate('/review-cards');
    }
  }, [orderDataFromState, navigate, orderData]);

  const fetchDigitalCardMaterial = async (cardId) => {
    try {
      // Fetch digital card details
      const response = await apiRequest(`${BACKEND_URL}/api/admin/public/digital-cards/${cardId}`, {
        method: 'GET',
      });

      if (response.ok && response.data) {
        const card = response.data;
        
        // If card has material_id, fetch material details
        if (card.material_id) {
          const materialResponse = await apiRequest(`${BACKEND_URL}/api/admin/materials`, {
            method: 'GET',
          });

          if (materialResponse.ok && materialResponse.data.materials) {
            const material = materialResponse.data.materials.find(m => m.id === card.material_id);
            if (material) {
              console.log('Setting material for digital card:', material);
              setSelectedMaterial(material);
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch digital card material:', err);
    }
  };

  // Re-validate the coupon whenever quantity or selected material changes
  // so the discount stays in sync with the current subtotal.
  // (Must be declared BEFORE the early return below to keep hook order stable.)
  useEffect(() => {
    if (!orderData || !appliedCoupon) return;
    const isDigital = orderData.type === 'digital-card';
    const unitPrice = isDigital
      ? (orderData.price || 50)
      : (selectedMaterial ? selectedMaterial.price : 122);
    const newSubtotal = unitPrice * quantity;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest(`${BACKEND_URL}/api/coupons/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: appliedCoupon.code,
            subtotal: newSubtotal,
            email: shippingAddress.email || (user && user.email) || null,
          }),
        });
        if (cancelled) return;
        if (res.ok && res.data?.valid) {
          setAppliedCoupon(res.data);
          setCouponError('');
        } else {
          setAppliedCoupon(null);
          setCouponError(res.data?.error || 'Coupon is no longer valid');
        }
      } catch {
        // Ignore transient errors
      }
    })();
    return () => { cancelled = true; };
  }, [quantity, selectedMaterial, orderData]);

  if (!orderData) {
    return null;
  }

  // Determine if this is a digital card or review card
  const isDigitalCard = orderData.type === 'digital-card';
  
  // Get product price
  let productPrice = 122; // Default price
  let productName = 'Review Card';
  
  if (isDigitalCard) {
    // Digital cards use price from selected color variant
    productPrice = orderData.price || 50;
    productName = orderData.cardName || 'Digital Card';
  } else {
    // Review cards have material-based pricing
    productPrice = selectedMaterial ? selectedMaterial.price : 122;
    productName = `${orderData.cardType} Card`;
  }
  
  const subtotal = productPrice * quantity;
  const shipping = 0;
  const discount = appliedCoupon?.discount_amount || 0;
  const total = Math.max(0, subtotal + shipping - discount);

  const handleApplyCoupon = async () => {
    setCouponError('');
    const code = (couponInput || '').trim();
    if (!code) {
      setCouponError('Please enter a coupon code');
      return;
    }
    if (subtotal <= 0) {
      setCouponError('Add an item before applying a coupon');
      return;
    }
    setCouponLoading(true);
    try {
      const res = await apiRequest(`${BACKEND_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          subtotal,
          email: shippingAddress.email || (user && user.email) || null,
        }),
      });
      if (res.ok && res.data?.valid) {
        setAppliedCoupon(res.data);
        setCouponInput(res.data.code);
        setCouponError('');
      } else {
        setAppliedCoupon(null);
        setCouponError(res.data?.error || 'Invalid coupon code');
      }
    } catch (err) {
      setCouponError(err.message || 'Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const handleMaterialChange = (e) => {
    const materialId = e.target.value;
    const material = orderData.card.materials.find(m => m.id === materialId);
    if (material) {
      setSelectedMaterial(material);
    }
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingAddress(prev => ({ ...prev, [name]: value }));
  };

  const MAX_QUANTITY = 5;

  const handleQuantityChange = (delta) => {
    setQuantity(Math.max(1, Math.min(MAX_QUANTITY, quantity + delta)));
  };

  const handleContinueToPayment = async (e) => {
    e.preventDefault();
    setError('');

    // Validate payment type
    if (!paymentType) {
      setError('Please select a payment method');
      return;
    }

    // Block online payment for now
    if (paymentType === 'online') {
      setError('Online payment is temporarily unavailable. Please select Cash on Delivery.');
      return;
    }

    setLoading(true);

    try {
      // If Cash on Delivery, create order directly without Stripe
      if (paymentType === 'cod') {
        // Each unit becomes its OWN order (so a unique dynamic link can be tied
        // to every quantity). We loop and create `quantity` orders, each with
        // quantity=1 and amount=productPrice.
        // Coupon (if applied) is attached to the FIRST order only — full discount
        // goes on that order, the rest are charged at full price.
        const createdOrderIds = [];

        for (let i = 0; i < quantity; i++) {
          const isFirst = i === 0;
          const orderCoupon = isFirst && appliedCoupon ? {
            code: appliedCoupon.code,
            name: appliedCoupon.name,
            discount_type: appliedCoupon.discount_type,
            discount_value: appliedCoupon.discount_value,
            discount_amount: appliedCoupon.discount_amount,
            max_cap: appliedCoupon.max_cap,
          } : null;
          const orderAmount = isFirst && appliedCoupon
            ? Math.max(0, productPrice - appliedCoupon.discount_amount)
            : productPrice;

          const response = await apiRequest(`${BACKEND_URL}/api/payments/create-cod-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              shipping_address: shippingAddress,
              billing_address: sameAsShipping ? shippingAddress : billingAddress,
              order_data: {
                ...orderData,
                quantity: 1,
                split_index: i + 1,
                split_total: quantity,
              },
              quantity: 1,
              amount: orderAmount,
              subtotal: productPrice,
              coupon: orderCoupon,
            }),
          });

          if (!response.ok) {
            throw new Error(
              response.data?.detail ||
                `Failed to create COD order ${i + 1} of ${quantity}`
            );
          }

          createdOrderIds.push(response.data.transaction_id);
        }

        // Redirect to success page (existing flow uses the first order id)
        navigate('/payment-success', {
          state: {
            orderId: createdOrderIds[0],
            orderIds: createdOrderIds,
            paymentType: 'cod',
          },
        });
        return;
      }

      // For online payment, proceed with Stripe
      // Prepare checkout data
      const checkoutData = {
        orderData: {
          ...orderData,
          quantity,
          selectedMaterial: selectedMaterial ? {
            id: selectedMaterial.id,
            name: selectedMaterial.name,
            price: selectedMaterial.price,
          } : null,
        },
        shippingAddress,
        billingAddress: sameAsShipping ? shippingAddress : billingAddress,
        amount: total,
        subtotal,
        coupon: appliedCoupon ? {
          code: appliedCoupon.code,
          name: appliedCoupon.name,
          discount_type: appliedCoupon.discount_type,
          discount_value: appliedCoupon.discount_value,
          discount_amount: appliedCoupon.discount_amount,
          max_cap: appliedCoupon.max_cap,
        } : null,
      };

      // Get frontend origin for success/cancel URLs
      const origin = window.location.origin;

      // Call backend to create Stripe checkout session using XMLHttpRequest
      const response = await apiRequest(`${BACKEND_URL}/api/payments/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          ...checkoutData,
          origin,
        }),
      });

      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to create checkout session');
      }

      // Redirect to Stripe checkout
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-extrabold text-navy mb-8">Checkout</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleContinueToPayment} className="grid lg:grid-cols-3 gap-8">
          {/* Left Side - Address Forms */}
          <div className="lg:col-span-2 space-y-8">
            {/* Shipping Address */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-navy mb-6">Shipping Address</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={shippingAddress.firstName}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={shippingAddress.lastName}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={shippingAddress.email}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent bg-gray-50"
                    required
                    readOnly
                    title="Email is auto-filled from your account"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from your account</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={shippingAddress.phone}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    name="addressLine1"
                    value={shippingAddress.addressLine1}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    name="addressLine2"
                    value={shippingAddress.addressLine2}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={shippingAddress.city}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={shippingAddress.state}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={shippingAddress.country}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={shippingAddress.zipCode}
                    onChange={handleShippingChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Billing Address */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-navy mb-6">Billing Address</h2>
              
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sameAsShipping}
                    onChange={(e) => setSameAsShipping(e.target.checked)}
                    className="w-4 h-4 text-navy focus:ring-navy rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Same as shipping address
                  </span>
                </label>
              </div>

              {!sameAsShipping && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={billingAddress.firstName}
                      onChange={handleBillingChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={billingAddress.lastName}
                      onChange={handleBillingChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-navy mb-2">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      name="addressLine1"
                      value={billingAddress.addressLine1}
                      onChange={handleBillingChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-navy mb-2">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      name="addressLine2"
                      value={billingAddress.addressLine2}
                      onChange={handleBillingChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={billingAddress.city}
                      onChange={handleBillingChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={billingAddress.state}
                      onChange={handleBillingChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={billingAddress.country}
                      onChange={handleBillingChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">
                      Zip Code
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      value={billingAddress.zipCode}
                      onChange={handleBillingChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Cart Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-8 shadow-sm sticky top-24">
              <h2 className="text-2xl font-bold text-navy mb-6">Cart</h2>

              {/* Product Details */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="font-bold text-navy mb-2">
                  {productName}
                </h3>

                {/* Digital Card Preview */}
                {isDigitalCard && orderData.cardImage && (
                  <div className="mb-4">
                    <img 
                      src={`${BACKEND_URL}${orderData.cardImage}`}
                      alt={orderData.cardName}
                      className="w-full rounded-lg border border-gray-200"
                    />
                  </div>
                )}

                {/* Digital Card Customization Details */}
                {isDigitalCard && (
                  <div className="text-sm text-gray-600 space-y-1 mb-4">
                    <p><strong>Name:</strong> {orderData.displayName}</p>
                    {orderData.companyName && <p><strong>Company:</strong> {orderData.companyName}</p>}
                    {orderData.jobTitle && <p><strong>Job Title:</strong> {orderData.jobTitle}</p>}
                    {orderData.phone && <p><strong>Phone:</strong> {orderData.phone}</p>}
                    {orderData.email && <p><strong>Email:</strong> {orderData.email}</p>}
                    {orderData.website && <p><strong>Website:</strong> {orderData.website}</p>}
                    {orderData.address && <p><strong>Address:</strong> {orderData.address}</p>}
                  </div>
                )}

                {/* Material Selection - Show only for Review Cards with multiple materials */}
                {!isDigitalCard && orderData.card && orderData.card.materials && orderData.card.materials.length > 1 && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-navy mb-2">
                      Select Material
                    </label>
                    <select
                      value={selectedMaterial?.id || ''}
                      onChange={handleMaterialChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    >
                      {orderData.card.materials.map((material) => (
                        <option key={material.id} value={material.id}>
                          {material.name} - {formatPrice(material.price)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Display selected material for Review Cards */}
                {!isDigitalCard && selectedMaterial && (
                  <p className="text-sm text-gray-600 mb-4">
                    Product Material - {selectedMaterial.name}
                  </p>
                )}

                <p className="text-2xl font-bold text-navy mb-4">
                  {formatPrice(productPrice)}
                </p>

                {/* Quantity Selector */}
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-gray-700">Quantity</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-12 text-center font-semibold">{quantity}</span>
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= MAX_QUANTITY}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Max {MAX_QUANTITY} per checkout. Each unit is created as a separate order so a unique dynamic link can be assigned to every card.
                </p>
              </div>

              {/* Coupon Section */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-sm font-bold text-navy mb-3">Have a coupon?</h3>
                {!appliedCoupon ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      data-testid="checkout-coupon-input"
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent text-sm uppercase"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleApplyCoupon();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponInput.trim()}
                      data-testid="checkout-coupon-apply-btn"
                      className="bg-navy text-white hover:bg-navy/90 rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
                    >
                      {couponLoading ? 'Applying…' : 'Apply'}
                    </Button>
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2"
                    data-testid="checkout-coupon-applied"
                  >
                    <div className="text-sm">
                      <p className="font-bold text-green-800">{appliedCoupon.code}</p>
                      <p className="text-xs text-green-700">
                        {appliedCoupon.discount_type === 'percentage'
                          ? `${appliedCoupon.discount_value}% off`
                          : `$${appliedCoupon.discount_value} off`}
                        {' — saved '}
                        <strong>${appliedCoupon.discount_amount?.toFixed(2)}</strong>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      data-testid="checkout-coupon-remove-btn"
                      className="text-xs font-semibold text-red-600 hover:text-red-800 underline"
                    >
                      Remove
                    </button>
                  </div>
                )}
                {couponError && (
                  <p className="text-xs text-red-600 mt-2" data-testid="checkout-coupon-error">
                    {couponError}
                  </p>
                )}
                {quantity > 1 && appliedCoupon && (
                  <p className="text-xs text-gray-500 mt-2">
                    Discount applies to the first card only. Remaining {quantity - 1} card{quantity - 1 > 1 ? 's' : ''} will be charged at full price.
                  </p>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal</span>
                  <span className="font-semibold" data-testid="checkout-subtotal">{formatPrice(subtotal)}</span>
                </div>
                {appliedCoupon && (
                  <div className="flex justify-between text-green-700">
                    <span>Coupon ({appliedCoupon.code})</span>
                    <span className="font-semibold" data-testid="checkout-discount">
                      -{formatPrice(discount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-gray-700">
                  <span>Shipping</span>
                  <span className="font-semibold">{formatPrice(shipping)}</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between text-lg font-bold text-navy">
                  <span>Total</span>
                  <span data-testid="checkout-total">{formatPrice(total)}</span>
                </div>
              </div>

              {/* Payment Type Selection */}
              <div className="mb-6 border border-gray-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-navy mb-4">Payment Type *</h3>
                
                <div className="space-y-3">
                  <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-not-allowed opacity-60 bg-gray-50 border-gray-200`}>
                    <input
                      type="radio"
                      name="paymentType"
                      value="online"
                      checked={paymentType === 'online'}
                      onChange={(e) => setPaymentType(e.target.value)}
                      disabled={true}
                      className="w-5 h-5 text-navy focus:ring-navy cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-gray-500 block">Online Payment</span>
                      <span className="text-xs text-gray-500">Pay securely with Stripe</span>
                      <span className="text-xs text-red-600 block mt-1">⚠️ Temporarily unavailable</span>
                    </div>
                  </label>

                  <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${paymentType === 'cod' ? 'border-navy bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="paymentType"
                      value="cod"
                      checked={paymentType === 'cod'}
                      onChange={(e) => setPaymentType(e.target.value)}
                      className="w-5 h-5 text-navy focus:ring-navy"
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-navy block">Cash on Delivery</span>
                      <span className="text-xs text-gray-600">Pay when you receive your order</span>
                    </div>
                  </label>
                </div>
                
                {!paymentType && (
                  <p className="text-xs text-red-600 mt-2">Please select a payment method</p>
                )}
              </div>

              {/* Continue to Payment Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-navy text-white hover:bg-navy/90 rounded-full py-6 text-base font-semibold disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Continue to Payment'}
              </Button>
            </div>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
}
